// src/pages/TagProductsPage.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronLeft, Loader2 } from 'lucide-react';
import apiClient from '../api/apiClient';
import VerticalContentCard from '../components/Category/VerticalContentCard';
import BookCard from '../components/library/BookCard';
import ContentFilters from '../components/common/ContentFilters';

const TagProductsPage = () => {
  const { tagSlug } = useParams();
  const [allBooks, setAllBooks] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [tagName, setTagName] = useState('');
  const [filters, setFilters] = useState({
    sortOrder: 'newest',
    isFree: null,
  });

  const [productsPage, setProductsPage] = useState(1);
  const [hasMoreProducts, setHasMoreProducts] = useState(true);
  const PRODUCTS_PAGE_SIZE = 10;

  const observerRef = useRef();
  const sentinelRef = useRef(null);

  const slugToTitle = (slug) => {
    return slug.split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  useEffect(() => {
    const fetchTagName = async () => {
      try {
        const response = await apiClient.get('/products/tags/');
        const tags = response.data;
        const foundTag = tags.find(t => t.slug === tagSlug);
        setTagName(foundTag ? foundTag.name : slugToTitle(tagSlug));
      } catch {
        setTagName(slugToTitle(tagSlug));
      }
    };
    fetchTagName();
  }, [tagSlug]);

  const fetchInitialData = useCallback(async () => {
    if (!tagSlug) return;
    setLoading(true);
    setError(null);

    try {
      const booksRes = await apiClient.get(`/books/`, {
        params: { tags__slug: tagSlug, limit: 1000 }
      });
      let books = [];
      if (Array.isArray(booksRes.data)) {
        books = booksRes.data;
      } else if (booksRes.data?.results) {
        books = booksRes.data.results;
      }
      setAllBooks(books);

      const params = { limit: PRODUCTS_PAGE_SIZE, offset: 0 };
      if (filters.sortOrder === 'newest') params.sort_by = 'newest';
      if (filters.isFree !== null) params.is_free = filters.isFree;

      const productsRes = await apiClient.get(`/products/list/tag/${tagSlug}/`, { params });
      let productsData = [];
      let total = 0;
      if (Array.isArray(productsRes.data)) {
        productsData = productsRes.data;
        total = productsData.length;
      } else if (productsRes.data?.results) {
        productsData = productsRes.data.results;
        total = productsRes.data.count || 0;
      }
      setProducts(productsData);
      setHasMoreProducts(productsData.length < total);
      setProductsPage(1);
    } catch {
      setError('خطا در بارگذاری آثار این تگ');
    } finally {
      setLoading(false);
    }
  }, [tagSlug, filters]);

  const loadMoreProducts = useCallback(async () => {
    if (!hasMoreProducts || loadingMore) return;
    setLoadingMore(true);

    try {
      const nextPage = productsPage + 1;
      const params = {
        limit: PRODUCTS_PAGE_SIZE,
        offset: (nextPage - 1) * PRODUCTS_PAGE_SIZE,
      };
      if (filters.sortOrder === 'newest') params.sort_by = 'newest';
      if (filters.isFree !== null) params.is_free = filters.isFree;

      const productsRes = await apiClient.get(`/products/list/tag/${tagSlug}/`, { params });
      let newProducts = [];
      let total = 0;
      if (Array.isArray(productsRes.data)) {
        newProducts = productsRes.data;
        total = newProducts.length;
      } else if (productsRes.data?.results) {
        newProducts = productsRes.data.results;
        total = productsRes.data.count || 0;
      }

      setProducts(prev => [...prev, ...newProducts]);
      setProductsPage(nextPage);
      setHasMoreProducts((nextPage * PRODUCTS_PAGE_SIZE) < total);
    } catch {
      // silently fail
    } finally {
      setLoadingMore(false);
    }
  }, [hasMoreProducts, loadingMore, productsPage, tagSlug, filters]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreProducts && !loading && !loadingMore) {
          loadMoreProducts();
        }
      },
      { threshold: 0.1 }
    );

    if (sentinelRef.current) {
      observerRef.current.observe(sentinelRef.current);
    }

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [hasMoreProducts, loading, loadingMore, loadMoreProducts]);

  const displayItems = [
    ...allBooks.map(book => ({ ...book, itemType: 'book' })),
    ...products.map(product => ({ ...product, itemType: 'product' })),
  ];

  const defaultOgImage = 'https://s3.afra.kubit.cloud/mahsacanva1365-far-free/profile/og-image.jpg';
  const pageTitle = tagName ? `آثار برچسب ${tagName} | فرزانه` : 'آثار برچسب | فرزانه';
  const pageDescription = tagName
    ? `لیست کتاب‌ها و محصولات مرتبط با برچسب «${tagName}» در فرزانه.`
    : 'مشاهده آثار بر اساس برچسب در فرزانه';

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-purple-700" />
      </div>
    );
  }

  if (error && displayItems.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-red-500 text-center p-4">{error}</div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={defaultOgImage} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={defaultOgImage} />
      </Helmet>

      <div className="sub-categories-page rtl container mx-auto p-0 pb-20 max-w-[460px] overflow-x-hidden bg-white">
        <div className="p-4 bg-white border-b border-gray-200">
          <nav className="text-[11px] font-light text-gray-500 mb-2 flex items-center space-x-reverse space-x-1">
            <Link to="/" className="hover:text-indigo-600">خانه</Link>
            <ChevronLeft size={14} className="mt-0.5 text-gray-400" />
            <span className="text-gray-800 font-medium">{tagName}</span>
          </nav>
          <h1 className="text-2xl font-extrabold text-gray-800 truncate">
             {tagName}
          </h1>
        </div>

        <ContentFilters filters={filters} setFilters={setFilters} />

        <div className="p-4 md:p-6">
          {displayItems.length === 0 ? (
            <div className="text-center text-gray-500 py-10">
              <p>هیچ اثری با این تگ یافت نشد.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {displayItems.map(item => (
                item.itemType === 'book' ? (
                  <BookCard key={`book-${item.id}`} book={item} />
                ) : (
                  <VerticalContentCard key={`product-${item.id}`} data={item} />
                )
              ))}
            </div>
          )}

          {hasMoreProducts && <div ref={sentinelRef} className="h-10" />}

          {loadingMore && (
            <div className="flex justify-center py-6">
              <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default TagProductsPage;